import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UserDetailsEntity, UserSecurityRoles, UserFeatures, UserAuthorizedRoles, UserAuthorizedFeatures } from 'private/app/models/user.model';
import { EditUserSecurityHelperService } from './edit-user-security-helper.service';
import { UserApiService } from 'private/app/services/account-admin/user/user-api.service';
import { BaseResponse } from 'common/models/account-status';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from 'common/services/toast.service';
import { ActivatedRoute } from '@angular/router';
import { UserAdminService } from 'private/app/services/account-admin/user/user-admin.service';

@Component({
    selector: 'hvac-edit-user-security',
    templateUrl: './edit-user-security.component.html',
    styleUrls: ['./edit-user-security.component.scss']
})
export class EditUserSecurityComponent implements OnInit {
    @Input() userDetails: UserDetailsEntity;
    @Output() cancelUserSecurityChanges = new EventEmitter();
    @Output() userSecurityUpdated = new EventEmitter<void>();
    public details!: UserDetailsEntity;

    public authorizedRoles: UserAuthorizedRoles[] = [];
    public authorizedFeatures: UserAuthorizedFeatures[] = [];
    public visibleAuthorizedRoles: UserAuthorizedRoles[] = [];

    public selectedRoleIds = new Set<number>();
    public selectedFeatureIds = new Set<number>();

    public currentStep: number = 1;
    public selectedRoleTab: number = 0;
    public selectedFeatureTab: number = 0;

    public currentRoles: UserSecurityRoles[] = [];
    public currentFeatures: UserFeatures[] = [];

    public companyId: string;
    public isContactOnlySelected: boolean;
    public isUserDetailsAvailable: boolean = true;
    public isUserDetailsSummeryMode: boolean = false;
    public userRoleAndFeatureDetails: boolean = false;
    public noAuthorizedRoles: boolean = false;
    public noAuthorizedFeatures: boolean = false;

    public selectedDesc: string = '';
    public selctedDisplayName: string = '';

    summary = {
        defaultRoles: [] as UserSecurityRoles[],
        selectedRoles: [] as UserSecurityRoles[],
        roleFeatures: [] as UserFeatures[],
        additionalFeatures: [] as UserFeatures[]
    };

    constructor(
        public userAPIService : UserApiService,
        private translate: TranslateService,
        private route: ActivatedRoute,
        public userAdminService: UserAdminService,
        private securityHelper: EditUserSecurityHelperService,
        private readonly toastService: ToastService
    ) {}

    get userFullName(): string {
        if (!this.details) {
            return '';
        }

        return `${this.details.firstName} ${this.details.lastName}`;
    }

    get companyInfo(): string {
        if (!this.details) {
            return '';
        }

        return `${this.details.companyId} - ${this.details.employerName}`;
    }

    get userIdToDisplay(): string {
        return this.details?.userID || '';
    }

    ngOnInit(): void {
        this.isUserDetailsAvailable = false;
        this.details = this.userDetails;
        this.companyId = this.userDetails?.companyId;
        this.isContactOnlySelected = this.userDetails?.contactOnly;
        this.loadData();
    }

    loadData(): void {
        this.noAuthorizedRoles = false;
        this.noAuthorizedFeatures = false;

        forkJoin([
            this.userAPIService.getUserAuthorizedRoles(this.companyId, this.isContactOnlySelected).pipe(
                catchError((error: HttpErrorResponse) => {
                    this.noAuthorizedRoles = error?.status === 404;
                    this.toastService.raiseDefaultErrorToast('EditUserSecurityComponent-getUserAuthorizedRoles', error?.error?.message);

                    return of([] as UserAuthorizedRoles[]);
                })
            ),
            this.userAPIService.getUserAuthorizedFeatures(this.companyId, this.isContactOnlySelected).pipe(
                catchError((error: HttpErrorResponse) => {
                    this.noAuthorizedFeatures = error?.status === 404;
                    this.toastService.raiseDefaultErrorToast('EditUserSecurityComponent-getUserAuthorizedFeatures', error?.error?.message);

                    return of([] as UserAuthorizedFeatures[]);
                })
            )
        ]).subscribe({
            next: ([roles, features]) => {
                this.authorizedRoles = this.normalizeAuthorizedList(roles);
                this.authorizedFeatures = this.normalizeAuthorizedList(features);
                this.visibleAuthorizedRoles = this.authorizedRoles.filter((category) => category.roleCategory !== 'System Defaults');
                this.isUserDetailsAvailable = true;
                this.initializeComponent();
            },
            error: () => {
                this.isUserDetailsAvailable = true;
            }
        });
    }

    initializeComponent(): void {
        const state = this.securityHelper.initializeSelections(this.details, this.authorizedRoles, this.authorizedFeatures);

        this.selectedRoleIds = state.selectedRoleIds;
        this.selectedFeatureIds = state.selectedFeatureIds;

        this.refreshView();
    }

    refreshView(): void {
        this.refreshCurrentRoles();
        this.refreshCurrentFeatures();
        this.rebuildSummary();
    }

    refreshCurrentRoles(): void {
        if (!this.visibleAuthorizedRoles.length) {
            this.currentRoles = [];

            return;
        }

        this.currentRoles = this.visibleAuthorizedRoles[this.selectedRoleTab]?.userRoles || [];
    }

    refreshCurrentFeatures(): void {
        if (!this.authorizedFeatures.length) {
            this.currentFeatures = [];

            return;
        }

        const roleFeatureIds = this.securityHelper.getRoleFeatureIds(
            this.details,
            this.authorizedRoles,
            this.selectedRoleIds
        );

        this.currentFeatures = this.authorizedFeatures[this.selectedFeatureTab]?.userFeatures.filter((feature) => !roleFeatureIds.has(feature.featureId)) || [];
    }

    rebuildSummary(): void {
        this.summary = this.securityHelper.buildSummary(this.details, this.authorizedRoles, this.authorizedFeatures, this.selectedRoleIds, this.selectedFeatureIds);
    }

    toggleRole(role: UserSecurityRoles): void {
        if (this.selectedRoleIds.has(role.roleId)) {
            this.selectedRoleIds.delete(role.roleId);
        }
        else {
            this.selectedRoleIds.add(role.roleId);
        }

        const roleFeatureIds = this.securityHelper.getRoleFeatureIds(this.details, this.authorizedRoles, this.selectedRoleIds);

        this.selectedFeatureIds = this.securityHelper.removeConflictingFeatures(this.selectedFeatureIds, roleFeatureIds);

        this.refreshView();
    }

    toggleFeature(feature: UserFeatures): void {
        if (this.selectedFeatureIds.has(feature.featureId)) {
            this.selectedFeatureIds.delete(feature.featureId);
        }
        else {
            this.selectedFeatureIds.add(feature.featureId);
        }

        this.rebuildSummary();
    }

    isRoleSelected(role: UserSecurityRoles): boolean {
        return this.selectedRoleIds.has(role.roleId);
    }

    isFeatureSelected(feature: UserFeatures): boolean {
        return this.selectedFeatureIds.has(feature.featureId);
    }

    onRoleTabChange(index: number): void {
        this.selectedRoleTab = index;
        this.refreshCurrentRoles();
    }

    onFeatureTabChange(index: number): void {
        this.selectedFeatureTab = index;
        this.refreshCurrentFeatures();
    }

    goNext(): void {
        this.currentStep = 2;
        this.refreshCurrentFeatures();
        this.userAdminService.smoothScrollToTop();
    }

    goBack(): void {
        this.currentStep = 1;
        this.refreshCurrentRoles();
        this.userAdminService.smoothScrollToTop();
    }

    submit(): void {
        const payload = this.securityHelper.buildSubmitPayload(this.details, this.summary);

        this.isUserDetailsAvailable = false;

        this.userAPIService.updateUserProfileDetailsV1(payload).subscribe((data:BaseResponse) => {
            this.isUserDetailsAvailable = true;
            if (data.status === this.translate.instant('USER_ADMIN.SUCCESS.USER_UPDATE_SUCCESS')) {
                this.toastService.add({
                    content: this.translate.instant('USER_ADMIN.SUCCESS.USER_UPDATE', { userID: this.route.snapshot.params.id }),
                    id: 'userUpdateSuccess',
                    theme: 'success',
                    closeable: true,
                    autoClose: true
                });
                this.userSecurityUpdated.emit();
                this.cancelUserSecurityChanges.emit(false);
            }
        },
        () => {
            this.isUserDetailsAvailable = true;
            this.toastService.add({
                content: this.translate.instant('USER_ADMIN.SUCCESS.USER_UPDATE_ERROR'),
                id: 'userUpdateError',
                theme: 'error',
                closeable: true,
                autoClose: true
            });
        });
    }

    trackByRole(_index: number, role: UserSecurityRoles): number {
        return role.roleId;
    }

    trackByFeature(_index: number, feature: UserFeatures): number {
        return feature.featureId;
    }

    getUserDetailsSummery(): void {
        this.isUserDetailsSummeryMode = !this.isUserDetailsSummeryMode;
    }

    cancelChanges(): void {
        this.cancelUserSecurityChanges.emit(false);
    }

    onSelectRoleAndFeatureDetails(role: UserSecurityRoles | null, feature: UserFeatures | null): void {
        if (role) {
            this.selctedDisplayName = role.roleDisplayName;
            this.selectedDesc = role.roleDesc || '';
        }

        if (feature) {
            this.selctedDisplayName = feature.featureDisplayName;
            this.selectedDesc = feature.featureDesc || '';
        }

        this.userRoleAndFeatureDetails = true;
    }

    userRoleAndFeatureModalClose(): void {
        this.userRoleAndFeatureDetails = false;
    }

    private normalizeAuthorizedList<T>(items: T[] | T | null | undefined): T[] {
        if (Array.isArray(items)) {
            return items;
        }

        if (items) {
            return [items];
        }

        return [];
    }
}
